import React from "react";
import { Link } from "react-router-dom";
import "./styles/UnifiedCommunications.css";
const channels = [
  {
    name: "Voice",
    text: "Business calling with a more modern and flexible cloud based communication experience.",
  },
  {
    name: "Messaging",
    text: "Team and business messaging that supports faster conversations and easier coordination.",
  },
  {
    name: "Video",
    text: "Video conferencing and online meetings for internal collaboration and client communication.",
  },
  {
    name: "Screen Sharing",
    text: "Share screens and visual context for better support, walkthroughs, and decision making.",
  },
];

const workflowSteps = [
  {
    step: "01",
    title: "Assess communication gaps",
    text: "Review where traditional voice tools, disconnected apps, or workflow delays are creating friction across teams.",
  },
  {
    step: "02",
    title: "Unify communication channels",
    text: "Bring calling, messaging, meetings, and collaboration into one environment that is easier to manage and easier to use.",
  },
  {
    step: "03",
    title: "Support day-to-day collaboration",
    text: "Give users a more connected platform for internal coordination, remote work, meetings, and client communication.",
  },
  {
    step: "04",
    title: "Scale as needs evolve",
    text: "Expand communication capabilities as teams grow, workflows change, and business operations become more distributed.",
  },
];

const useCases = [
  {
    title: "Hybrid teams",
    text: "Keep office based and remote users connected with one communication layer across devices and locations.",
  },
  {
    title: "Client facing teams",
    text: "Support sales, account, and service conversations through integrated voice, chat, and meeting tools.",
  },
  {
    title: "Growing businesses",
    text: "Standardize communication workflows early with a platform that can support expansion more smoothly.",
  },
];

const faqs = [
  {
    q: "What makes UCaaS different from traditional VoIP?",
    a: "Traditional VoIP focuses mainly on voice calling, while UCaaS combines voice with messaging, chats, video meetings, conferencing, and collaboration tools in one connected environment.",
  },
  {
    q: "Who is this service best suited for?",
    a: "It is a strong fit for businesses that want to modernize communication, support hybrid work, and reduce reliance on disconnected tools.",
  },
  {
    q: "Why does it matter for business operations?",
    a: "A unified communication platform can improve responsiveness, reduce communication friction, and make collaboration more consistent across teams and locations.",
  },
];

export default function UnifiedCommunications() {
  return (
    <div className="ucaasPageAlt">
      <section className="ucaasAltHero">
        <div className="ucaasAltContainer ucaasAltHero__container">
          <div className="ucaasAltHero__content">
            <p className="ucaasAltEyebrow">Unified Communications-as-a-Service</p>

            <h1 className="ucaasAltHero__title">
              Move beyond voice only systems and connect every conversation.
            </h1>

            <p className="ucaasAltHero__text">
              Unified Communications as a Service transforms traditional VoIP
              into a multi channel communication platform by integrating voice,
              messaging, chats, video conferencing, screen sharing, and online
              meetings. Instead of relying on separate tools, businesses gain a
              more connected communication experience that supports modern work,
              faster collaboration, and better responsiveness.
            </p>

            <div className="ucaasAltHero__actions">
              <Link to="/contact" className="ucaasAltBtn ucaasAltBtn--primary">
                Talk to Our Team
              </Link>
              <Link to="/services" className="ucaasAltBtn ucaasAltBtn--secondary">
                Explore Services
              </Link>
            </div>
          </div>

          <div className="ucaasAltHero__board">
            <div className="ucaasAltBoard">
              <div className="ucaasAltBoard__top">
                <span className="ucaasAltBadge">UCaaS Platform</span>
                <span className="ucaasAltBadge ucaasAltBadge--soft">
                  Multi channel
                </span>
              </div>

              <div className="ucaasAltBoard__grid">
                {channels.map((channel) => (
                  <div key={channel.name} className="ucaasAltBoard__cell">
                    <strong>{channel.name}</strong>
                    <span>{channel.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="ucaasAltSection ucaasAltSection--channels">
        <div className="ucaasAltContainer">
          <div className="ucaasAltSectionHead ucaasAltSectionHead--center">
            <p className="ucaasAltEyebrow">Included communication channels</p>
            <h2 className="ucaasAltTitle">
              One platform across the ways teams actually communicate
            </h2>
            <p className="ucaasAltText">
              UCaaS creates a more complete communication environment by
              connecting the tools businesses use most often instead of treating
              voice as a standalone system.
            </p>
          </div>

          <div className="ucaasAltChannelStrip">
            {channels.map((channel, index) => (
              <article key={channel.name} className="ucaasAltChannelCard">
                <span className="ucaasAltChannelCard__index">
                  0{index + 1}
                </span>
                <h3>{channel.name}</h3>
                <p>{channel.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ucaasAltSection ucaasAltSection--comparison">
        <div className="ucaasAltContainer">
          <div className="ucaasAltCompare">
            <div className="ucaasAltCompare__old">
              <p className="ucaasAltEyebrow">Traditional approach</p>
              <h2 className="ucaasAltTitle">Disconnected communication workflows</h2>
              <ul className="ucaasAltList ucaasAltList--muted">
                <li>Voice handled separately from messaging and meetings</li>
                <li>Different tools for different communication moments</li>
                <li>More friction for hybrid users and distributed teams</li>
                <li>Less consistency across internal and external communication</li>
              </ul>
            </div>

            <div className="ucaasAltCompare__divider">
              <span>vs</span>
            </div>

            <div className="ucaasAltCompare__new">
              <p className="ucaasAltEyebrow">UCaaS model</p>
              <h2 className="ucaasAltTitle">A more connected communication layer</h2>
              <ul className="ucaasAltList">
                <li>Voice, messaging, meetings, and chat together</li>
                <li>Fewer gaps between communication channels</li>
                <li>Better support for modern business workflows</li>
                <li>Stronger collaboration across teams and locations</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="ucaasAltSection ucaasAltSection--workflow">
        <div className="ucaasAltContainer">
          <div className="ucaasAltSectionHead">
            <p className="ucaasAltEyebrow">How it works in practice</p>
            <h2 className="ucaasAltTitle">
              A communication model that improves with each stage
            </h2>
            <p className="ucaasAltText">
              UCaaS is not just a phone replacement. It is a more connected way
              to structure communication across users, channels, and workflows.
            </p>
          </div>

          <div className="ucaasAltTimeline">
            {workflowSteps.map((item) => (
              <article key={item.step} className="ucaasAltTimeline__item">
                <div className="ucaasAltTimeline__step">{item.step}</div>
                <div className="ucaasAltTimeline__content">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ucaasAltSection ucaasAltSection--usecases">
        <div className="ucaasAltContainer">
          <div className="ucaasAltSectionHead ucaasAltSectionHead--center">
            <p className="ucaasAltEyebrow">Best fit environments</p>
            <h2 className="ucaasAltTitle">Where this service creates the most value</h2>
            <p className="ucaasAltText">
              UCaaS fits organizations that need communication flexibility,
              clearer collaboration, and a more modern platform for ongoing work.
            </p>
          </div>

          <div className="ucaasAltUseCases">
            {useCases.map((item) => (
              <article key={item.title} className="ucaasAltUseCaseCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="ucaasAltSection ucaasAltSection--faq">
        <div className="ucaasAltContainer">
          <div className="ucaasAltFaqWrap">
            <div className="ucaasAltFaqIntro">
              <p className="ucaasAltEyebrow">Common questions</p>
              <h2 className="ucaasAltTitle">UCaaS in simple terms</h2>
              <p className="ucaasAltText">
                Here is a simpler way to explain what this service offers and why
                it matters for modern businesses.
              </p>
            </div>

            <div className="ucaasAltFaqList">
              {faqs.map((item) => (
                <article key={item.q} className="ucaasAltFaqItem">
                  <h3>{item.q}</h3>
                  <p>{item.a}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="ucaasAltCta">
        <div className="ucaasAltContainer ucaasAltCta__container">
          <div>
            <p className="ucaasAltEyebrow ucaasAltEyebrow--light">Next step</p>
            <h2 className="ucaasAltCta__title">
              Build a more flexible communication environment for your business
            </h2>
            <p className="ucaasAltCta__text">
              If your team is ready to move beyond voice only systems and adopt
              a communication platform built for messaging, meetings, and modern
              collaboration, ComNetworks.net can help you take the next step.
            </p>
          </div>

          <div className="ucaasAltCta__actions">
            <Link to="/contact" className="ucaasAltBtn ucaasAltBtn--primary">
              Contact Us
            </Link>
            <Link to="/services" className="ucaasAltBtn ucaasAltBtn--light">
              View More Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
